import { CSSProperties, useContext, useState } from 'react';
import {
  Box,
  SxProps,
  Tooltip,
  Typography,
  emphasize,
  lighten
} from '@mui/material';
import { faBicycle, faCar } from '@fortawesome/pro-light-svg-icons';
import { DARK, RED } from 'constants/colors';
import { clipText } from 'utils/misc';
import {
  PopoverWarningDialog,
  RouteContextMenu,
  ScheduleTooltipBody
} from '../../components';
import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';
import { Event, Schedule } from '../../types';
import { useRecoilState } from 'recoil';
import { shouldShowPopoverDialogAtom } from '../../atoms';
import { PopOverConfirmationContext } from '../../PopOverConfirmationContext';
import { EditAppointmentType } from 'RestAPIs/activities/types';
import AbsenceTooltipBody from '../../components/AbsenceTooltipBody';
import { AbsenceResponse, AvailabilityResponse } from 'generated/index';
import dayjs from 'dayjs';

interface AppointmentContextProps {
  type: 'none' | 'absence' | 'schedule' | 'availability' | 'unavailability';
  label: string;
  startingPoint: number;
  width: number;
  schedule?: Schedule;
  absence?: AbsenceResponse;
  availability?: AvailabilityResponse;
  connectAppointmentGrouping?: (
    appointmentGroupingEntityId: string,
    editAppointmentType?: EditAppointmentType,
    scheduleStart?: string
  ) => void;
  sx?: SxProps;
}

const AppointmentContext = ({
  type,
  label,
  startingPoint,
  width,
  schedule,
  absence,
  connectAppointmentGrouping,
  sx
}: AppointmentContextProps) => {
  const [
    {
      id: popoverOccurenceId,
      scheduleStart: popoverScheduleStart,
      isRouteConnect
    },
    setShouldShowPopoverDialog
  ] = useRecoilState(shouldShowPopoverDialogAtom);

  const { isLoading, onClose, onHandleAppointmentConfirmation } = useContext(
    PopOverConfirmationContext
  );

  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0
  }); // For contextmenu
  const {
    appointmentGrouping,
    name,
    date,
    employee,
    transportationMode = 'CAR',
    events = [],
    planningEntityId,
    start
  } = schedule || {};

  const getColorScheme = (): `#${string}` => {
    switch (type) {
      case 'absence':
      case 'unavailability':
        return lighten(RED, 0.9) as `#${string}`;
      case 'availability':
        return '#ACE1DF';
      case 'none':
        return '#F7F7F7';
      case 'schedule':
        return '#D2EBF0';
    }
  };

  const fontStyle: CSSProperties = {
    color: emphasize(getColorScheme(), 0.5),
    fontSize: '10px',
    fontFamily: 'signikaregular',
    whiteSpace: 'nowrap'
  };

  const hasScheduleStart = !!(popoverScheduleStart && start);

  // We compare only the YYYY-MM-DD of the dates, not the time.
  const popoverScheduleStartDate: string = dayjs(popoverScheduleStart).isValid()
    ? dayjs(popoverScheduleStart).format('YYYY-MM-DD')
    : '';
  const currentScheduleStartDate: string = dayjs(start).isValid()
    ? dayjs(start).format('YYYY-MM-DD')
    : '';

  const showPopoverDialog: boolean =
    isRouteConnect &&
    hasScheduleStart &&
    planningEntityId === popoverOccurenceId
      ? popoverScheduleStartDate === currentScheduleStartDate
      : false;

  const getHasClientLocation = (event: Event) => {
    const { category, location } = event;
    return (
      category == 'CLIENT' &&
      location?.locationType === 'CLIENTADDRESS' &&
      (location?.address || (location?.latitude && location?.longitude))
    );
  };

  return (
    <PopoverWarningDialog
      open={showPopoverDialog && !isLoading}
      onClose={() => {
        onClose?.();
        setShouldShowPopoverDialog({ id: '', isRouteConnect: false });
      }}
      onClickAppointment={(editAppointmentType: EditAppointmentType) => {
        onHandleAppointmentConfirmation?.(editAppointmentType);
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '5px',
          zIndex: 0,
          left: startingPoint,
          width,
          height: '35px',
          padding: '4px',
          backgroundColor: getColorScheme(),
          boxSizing: 'border-box',
          borderRadius: '4px',
          boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.075)',
          ...(sx || {})
        }}
      >
        <Tooltip
          disableHoverListener={
            type === 'availability' || type === 'unavailability'
          }
          title={
            type === 'absence' ? (
              <AbsenceTooltipBody type={absence?.type} />
            ) : (
              <ScheduleTooltipBody
                employeeName={employee?.nameInformal}
                appointmentGrouping={appointmentGrouping}
                planningFeatures={employee?.planningFeatures}
              />
            )
          }
          placement="top"
          arrow
        >
          <Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <span style={fontStyle}>
                {/* We assume 1 character is 8px wide, and only show text if the width is more than 10px */}
                {width >= 10 ? clipText(label, width / 8) : ''}
              </span>

              {type === 'schedule' &&
                (schedule?.events || []).filter(getHasClientLocation).length >
                  1 &&
                width >= 140 && (
                  <Box
                    display="flex"
                    sx={{
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <FontAwesomeSvgIcon
                      icon={
                        schedule?.transportationMode === 'CAR'
                          ? faCar
                          : faBicycle
                      }
                      sx={{
                        color: DARK,
                        fontSize: '12px',
                        marginRight: '5px'
                      }}
                    />

                    <Typography
                      sx={{
                        color: DARK,
                        fontSize: '10px',
                        fontWeight: '300'
                      }}
                    >
                      {schedule?.metrics?.travelKilometers ?? '-'} km
                    </Typography>
                  </Box>
                )}
            </Box>

            {schedule && type === 'schedule' && (
              <RouteContextMenu
                appointmentGroupingEntityId={
                  appointmentGrouping?.entityId || ''
                }
                appointmentGroupingName={name}
                appointmentGroupingStart={date || ''}
                onConnectClick={() =>
                  connectAppointmentGrouping?.(
                    appointmentGrouping?.entityId || '',
                    undefined,
                    start
                  )
                }
                onChangeMousePosition={(x: number, y: number) =>
                  setMousePosition({ x, y })
                }
                transportationMode={transportationMode}
                events={events}
                employeeId={employee?.id}
                clickType="rmb"
                scheduleId={schedule.planningEntityId}
                sx={{
                  // Override three-dot button
                  button: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: 0,
                    opacity: 0
                  }
                }}
                menuSx={{
                  '.MuiMenu-paper': {
                    top: `${mousePosition.y}px !important`,
                    left: `${mousePosition.x}px !important`
                  }
                }}
                isAssignableToEmployee={!!schedule?.isAssignableToEmployee}
              />
            )}
          </Box>
        </Tooltip>
      </Box>
    </PopoverWarningDialog>
  );
};

export default AppointmentContext;
